/**
 * Keeps the PDP gift threshold bar in step with the cart.
 *
 * Liquid renders the first state from the qualifying cart total (excluding
 * gift lines); this element re-renders it on the cart engine's `cart:change`
 * event (assets/cart.js), because the bar sits on the product page and the
 * cart drawer can change the subtotal without a reload.
 *
 * Auto-add / remove is owned by `<gift-threshold-sync>`, not this element.
 *
 * The wording comes from data attributes holding the already-translated strings
 * with their `{{amount}}` placeholder intact, so no copy is duplicated here. The
 * money formatter reads `shop.money_format`, so the amounts match the ones
 * Liquid printed rather than a locale guess. Those templates may be merchant
 * inline richtext, so they are written with `innerHTML`; `[gift]` is already
 * resolved by Liquid, and only `[amount]` is substituted here.
 */

/**
 * @param {number} cents - Amount in the store's minor currency unit
 * @param {string} format - shop.money_format, e.g. "$ {{amount}}"
 * @returns {string}
 */
function formatMoney(cents, format) {
  const value = Number(cents) || 0;

  const withDecimals = (separator, thousands) => {
    const units = Math.floor(Math.abs(value) / 100).toString();
    const grouped = units.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const fraction = (Math.abs(value) % 100).toString().padStart(2, '0');
    return separator === null ? grouped : `${grouped}${separator}${fraction}`;
  };

  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, placeholder) => {
    switch (placeholder) {
      case 'amount_no_decimals':
        return withDecimals(null, ',');
      case 'amount_with_comma_separator':
        return withDecimals(',', '.');
      case 'amount_no_decimals_with_comma_separator':
        return withDecimals(null, '.');
      default:
        return withDecimals('.', ',');
    }
  });
}

const GIFT_PROP = '_gift_threshold';

/**
 * @param {object | null | undefined} cart
 * @returns {number}
 */
function qualifyingTotal(cart) {
  if (!cart?.items?.length) {
    return 0;
  }

  return cart.items.reduce((sum, item) => {
    const value = item?.properties?.[GIFT_PROP];
    if (value === 'true' || value === true) {
      return sum;
    }
    return sum + (item.final_line_price || 0);
  }, 0);
}

if (!customElements.get('gift-threshold-bar')) {
  customElements.define(
    'gift-threshold-bar',
    class GiftThresholdBar extends HTMLElement {
      constructor() {
        super();
        this.onCartChange = this.onCartChange.bind(this);
      }

      connectedCallback() {
        document.addEventListener('cart:change', this.onCartChange);
      }

      disconnectedCallback() {
        document.removeEventListener('cart:change', this.onCartChange);
      }

      /** @param {CustomEvent} event - cart:change from assets/cart.js */
      onCartChange(event) {
        const cart = event.detail?.cart;
        if (!cart) {
          return;
        }
        this.render(qualifyingTotal(cart));
      }

      /** @param {number} total - Cart subtotal in cents */
      render(total) {
        // Drop the space the store's format puts before the number ("$ {{amount}}"),
        // matching snippets/component-money-tight.liquid on first render.
        const format = (this.dataset.moneyFormat || '{{amount}}').replace(/\s+(?=\{\{)/, '');
        const shipping = Number(this.dataset.shippingThreshold) || 0;
        const gift = Number(this.dataset.giftThreshold) || 0;
        const trackMax = Number(this.dataset.trackMax) || 0;

        const progress = this.querySelector('[data-gift-bar-progress]');
        if (progress) {
          progress.value = Math.min(total, trackMax);
        }

        // innerHTML, not textContent: the copy settings are inline richtext, so
        // the templates can carry <strong>/<em>/<a>. Shopify sanitises them to
        // that tag set before they ever reach the data attribute.
        const headline = this.querySelector('[data-gift-bar-headline]');
        if (headline) {
          headline.innerHTML = this.headlineFor(total, shipping, gift, format);
        }

        const inCart = this.querySelector('[data-gift-bar-in-cart]');
        if (inCart) {
          inCart.innerHTML = fillAmount(this.dataset.copyInCart || '', total, format);
        }

        for (const stop of this.querySelectorAll('[data-gift-bar-stop]')) {
          stop.toggleAttribute('data-reached', total >= Number(stop.dataset.giftBarStop));
        }
      }

      /**
       * @param {number} total - Cart subtotal in cents
       * @param {number} shipping - Free shipping threshold in cents, 0 when unset
       * @param {number} gift - Free gift threshold in cents, 0 when unset
       * @param {string} format - shop.money_format
       * @returns {string}
       */
      headlineFor(total, shipping, gift, format) {
        if (shipping > 0 && total < shipping) {
          return fillAmount(this.dataset.copyAwayShipping || '', shipping - total, format);
        }
        if (gift > 0 && total < gift) {
          return fillAmount(this.dataset.copyAwayGift || '', gift - total, format);
        }
        return gift > 0 ? this.dataset.copyAllUnlocked || '' : this.dataset.copyShippingUnlocked || '';
      }
    },
  );
}

/**
 * @param {string} template
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function fillAmount(template, cents, format) {
  const money = formatMoney(cents, format);
  return template.replaceAll('[amount]', money).replaceAll('{{amount}}', money);
}
